import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../utils/supabaseClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    checkSession();
    
    // Listen for auth state changes (for OAuth)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session);
      
      if (event === 'SIGNED_IN' && session) {
        await handleOAuthUser(session);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setIsPremium(false);
        localStorage.removeItem('session_token');
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const handleOAuthUser = async (session) => {
    try {
      const { user: supabaseUser } = session;
      
      // Safety check for supabaseUser
      if (!supabaseUser || !supabaseUser.email) {
        console.error('Invalid Supabase user data:', supabaseUser);
        return;
      }
      
      // Check if user exists in our custom users table
      let { data: existingUser, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', supabaseUser.email)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!existingUser) {
        // Create new user in our custom table
        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert({
            email: supabaseUser.email,
            full_name: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || '',
            avatar_url: supabaseUser.user_metadata?.avatar_url || supabaseUser.user_metadata?.picture || null,
            auth_provider: supabaseUser.app_metadata?.provider || 'google',
            provider_id: supabaseUser.id,
            email_verified: supabaseUser.email_confirmed_at ? true : false,
            provider_data: {
              avatar_url: supabaseUser.user_metadata?.avatar_url || supabaseUser.user_metadata?.picture,
              provider_name: supabaseUser.user_metadata?.iss || 'Google'
            }
          })
          .select()
          .single();

        if (insertError) {
          throw insertError;
        }
        
        existingUser = newUser;
      } else {
        // Update existing user's last login and provider data
        await supabase
          .from('users')
          .update({ 
            last_login_at: new Date().toISOString(),
            avatar_url: supabaseUser.user_metadata?.avatar_url || supabaseUser.user_metadata?.picture || existingUser.avatar_url,
            provider_data: {
              ...existingUser.provider_data,
              avatar_url: supabaseUser.user_metadata?.avatar_url || supabaseUser.user_metadata?.picture,
              provider_name: supabaseUser.user_metadata?.iss || 'Google'
            }
          })
          .eq('id', existingUser.id);
      }

      // Safety check for existingUser
      if (!existingUser || !existingUser.id) {
        console.error('Failed to get or create user:', existingUser);
        return;
      }

      setUser(existingUser);
      await checkPremiumStatus(existingUser.id);
      
      // Create a session token for consistency with email auth
      const sessionToken = generateSessionToken();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      await supabase
        .from('user_sessions')
        .insert({
          user_id: existingUser.id,
          session_token: sessionToken,
          expires_at: expiresAt.toISOString(),
          ip_address: await getUserIP(),
          user_agent: navigator.userAgent
        });

      localStorage.setItem('session_token', sessionToken);
      
    } catch (error) {
      console.error('Error handling OAuth user:', error);
    }
  };

  const checkSession = async () => {
    try {
      // First check Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await handleOAuthUser(session);
        return;
      }

      // Then check custom session
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) {
        setLoading(false);
        return;
      }

      // Verify session with backend - use simpler query to avoid 406 errors
      const { data: sessionData, error: sessionError } = await supabase
        .from('user_sessions')
        .select('user_id, expires_at')
        .eq('session_token', sessionToken)
        .gt('expires_at', new Date().toISOString())
        .single();

      if (sessionError || !sessionData || !sessionData.user_id) {
        // Session expired or invalid
        console.log('Session invalid or expired, clearing...', { sessionError, sessionData });
        localStorage.removeItem('session_token');
        setLoading(false);
        return;
      }

      // Now fetch user data separately
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, full_name, avatar_url, is_premium, premium_expires_at, total_rooms_created, total_games_hosted, auth_provider')
        .eq('id', sessionData.user_id)
        .single();

      if (userError || !userData) {
        console.error('Error fetching user data:', userError);
        localStorage.removeItem('session_token');
        setLoading(false);
        return;
      }

      setUser(userData);
      await checkPremiumStatus(userData.id);
    } catch (error) {
      console.error('Error checking session:', error);
      localStorage.removeItem('session_token');
    } finally {
      setLoading(false);
    }
  };

  const checkPremiumStatus = async (userId) => {
    try {
      const { data, error } = await supabase
        .rpc('is_user_premium', { user_uuid: userId });

      if (!error) {
        setIsPremium(data);
      }
    } catch (error) {
      console.error('Error checking premium status:', error);
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        }
      });

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Google sign in error:', error);
      return { success: false, error: error.message };
    }
  };

  const login = async (email, password) => {
    try {
      // Hash password and authenticate user
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email.toLowerCase())
        .eq('auth_provider', 'email') // Only match email auth users
        .single();

      if (userError || !userData) {
        console.error('User lookup error:', userError);
        throw new Error('Invalid email or password');
      }

      // Verify password hash
      const passwordHash = await hashPassword(password);
      if (userData.password_hash !== passwordHash) {
        throw new Error('Invalid email or password');
      }
      
      // Create new session
      const sessionToken = generateSessionToken();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30); // 30 days

      const { error: sessionError } = await supabase
        .from('user_sessions')
        .insert({
          user_id: userData.id,
          session_token: sessionToken,
          expires_at: expiresAt.toISOString(),
          ip_address: await getUserIP(),
          user_agent: navigator.userAgent
        });

      if (sessionError) {
        throw new Error('Failed to create session');
      }

      // Update last login
      await supabase
        .from('users')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', userData.id);

      localStorage.setItem('session_token', sessionToken);
      setUser(userData);
      await checkPremiumStatus(userData.id);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signup = async (email, password, fullName) => {
    try {
      // Check if user already exists
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id')
        .eq('email', email.toLowerCase())
        .single();

      // Ignore error if user doesn't exist (expected case)
      if (existingUser) {
        throw new Error('User already exists with this email');
      }

      // In a real app, you'd hash the password here
      // For demo purposes, we'll store a placeholder hash
      const passwordHash = await hashPassword(password);

      console.log('Attempting to create user with:', {
        email: email.toLowerCase(),
        full_name: fullName,
        auth_provider: 'email'
      });

      // Create new user
      const { data: newUser, error: userError } = await supabase
        .from('users')
        .insert({
          email: email.toLowerCase(),
          password_hash: passwordHash,
          full_name: fullName,
          auth_provider: 'email',
          email_verified: false, // In production, send verification email
          provider_data: {} // Add default empty object for OAuth fields
        })
        .select()
        .single();

      if (userError) {
        console.error('Database error creating user:', userError);
        throw new Error(`Failed to create account: ${userError.message}`);
      }

      console.log('User created successfully:', newUser);

      // Automatically log in the new user
      return await login(email, password);
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Sign out from Supabase (for OAuth users)
      await supabase.auth.signOut();
      
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        // Remove session from database
        await supabase
          .from('user_sessions')
          .delete()
          .eq('session_token', sessionToken);
      }

      localStorage.removeItem('session_token');
      setUser(null);
      setIsPremium(false);
    } catch (error) {
      console.error('Error during logout:', error);
      // Still clear local state even if API call fails
      localStorage.removeItem('session_token');
      setUser(null);
      setIsPremium(false);
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');

      const { error } = await supabase
        .from('users')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      // Update local user state
      setUser(prev => ({ ...prev, ...updates }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const getFeatureLimit = async (featureName) => {
    try {
      if (!user) return 0;

      const { data, error } = await supabase
        .rpc('get_user_feature_limit', {
          user_uuid: user.id,
          feature_name_param: featureName
        });

      if (error) throw error;
      return data || 0;
    } catch (error) {
      console.error('Error getting feature limit:', error);
      return 0;
    }
  };

  // Helper functions
  const generateSessionToken = () => {
    return 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
  };

  const getUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return null;
    }
  };

  const hashPassword = async (password) => {
    // In production, use bcrypt or similar
    // For demo purposes, just return a placeholder
    return '$2b$10$demo_hash_' + btoa(password);
  };

  const value = {
    user,
    loading,
    isPremium,
    login,
    signup,
    signInWithGoogle,
    logout,
    updateProfile,
    getFeatureLimit,
    checkPremiumStatus: () => checkPremiumStatus(user?.id)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 